/**
 * Gmail-style conversation threading helpers.
 */

export const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

export const normalizeMessageId = (value) => String(value || '').trim().replace(/^<|>$/g, '');

export const extractEmailAddress = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const angle = raw.match(/<([^>]+)>/);
  if (angle && angle[1]) return angle[1].trim().toLowerCase();

  const emailMatch = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  return emailMatch ? emailMatch[0].toLowerCase() : raw.toLowerCase();
};

export const normalizeSubject = (subject) => {
  let s = String(subject || '').trim();
  let changed = true;
  while (changed) {
    changed = false;
    const next = s.replace(/^(re|fwd|fw):\s*/i, '').trim();
    if (next !== s) {
      s = next;
      changed = true;
    }
  }
  return s.toLowerCase();
};

export const getCorrespondent = (mail, userEmail) => {
  const me = extractEmailAddress(userEmail);
  const from = extractEmailAddress(mail?.from);
  const firstTo = extractEmailAddress(String(mail?.to || '').split(',')[0] || '');

  if (from && from.includes('@') && from !== me) return from;
  if (firstTo && firstTo.includes('@') && firstTo !== me) return firstTo;
  return from || firstTo || '';
};

const buildMessageIdIndex = (mails = []) => {
  const index = new Map();
  for (const mail of mails) {
    const messageId = normalizeMessageId(mail?.messageId);
    if (messageId) index.set(messageId, mail);
  }
  return index;
};

export const resolveThreadKey = (mail, userEmail, mailByMessageId, visiting = new Set()) => {
  if (!mail) return 'empty';

  const explicitThreadId = String(mail?.threadId || '').trim();
  if (explicitThreadId) return `tid:${explicitThreadId}`;

  const messageId = normalizeMessageId(mail?.messageId);
  if (messageId && visiting.has(messageId)) {
    return `solo:${messageId}`;
  }

  const inReplyTo = normalizeMessageId(mail?.inReplyTo);
  if (inReplyTo && mailByMessageId?.has(inReplyTo)) {
    const parent = mailByMessageId.get(inReplyTo);
    const nextVisiting = new Set(visiting);
    if (messageId) nextVisiting.add(messageId);
    return resolveThreadKey(parent, userEmail, mailByMessageId, nextVisiting);
  }

  if (messageId) return `solo:${messageId}`;

  const uid = mail?.uid;
  if (uid != null) return `solo:imap-${uid}`;

  const localId = mail?._id;
  if (localId && !String(localId).startsWith('imap-') && !String(localId).startsWith('temp-')) {
    return `solo:db:${localId}`;
  }

  const subject = normalizeSubject(mail?.subject);
  const correspondent = getCorrespondent(mail, userEmail);
  return `legacy:${subject}::${correspondent}`;
};

export const getThreadKey = (mail, userEmail, mailByMessageId) =>
  resolveThreadKey(mail, userEmail, mailByMessageId);

export const getMailTimestamp = (mail) => {
  const value = mail?.createdAt || mail?.date;
  const time = value ? new Date(value).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
};

const getMailOrderValue = (mail) => {
  const uid = Number(mail?.uid ?? 0);
  if (!Number.isNaN(uid) && uid > 0) return uid;
  const idTime = new Date(mail?.createdAt || mail?.date || 0).getTime();
  return Number.isNaN(idTime) ? 0 : idTime;
};

export const getMailDedupeKey = (mail) => {
  if (mail?.uid != null) return `imap:${mail.uid}`;
  if (mail?._id) return `db:${mail._id}`;
  if (mail?.id) return `db:${mail.id}`;
  return `tmp:${mail?.from || ''}:${mail?.subject || ''}:${getMailTimestamp(mail)}`;
};

export const extractReplyText = (body) => {
  const raw = String(body || '').replace(/\r\n/g, '\n');
  const markerMatch = raw.match(/(\n\n|\n)(On .+ wrote:)\s*/i);
  if (markerMatch) {
    const markerIndex = raw.indexOf(markerMatch[2]);
    if (markerIndex > 0) {
      return raw.slice(0, markerIndex).trim();
    }
  }
  return raw.trim();
};

const mergeMailDetails = (base, incoming) => ({
  ...base,
  ...incoming,
  body: incoming.body || base.body,
  text: incoming.text || base.text,
  html: incoming.html || base.html,
  preview: incoming.preview || base.preview,
  threadId: incoming.threadId || base.threadId,
  messageId: incoming.messageId || base.messageId,
  inReplyTo: incoming.inReplyTo || base.inReplyTo,
  attachments: incoming.attachments?.length ? incoming.attachments : base.attachments,
});

export const buildThreadMessages = (rootMail, inboxMails = [], sentMails = [], userEmail) => {
  if (!rootMail) return [];
  const pool = [...(inboxMails || []), ...(sentMails || []), rootMail].filter(Boolean);
  const mailByMessageId = buildMessageIdIndex(pool);
  const threadKey = resolveThreadKey(rootMail, userEmail, mailByMessageId);
  const matches = pool.filter(
    (mail) => resolveThreadKey(mail, userEmail, mailByMessageId) === threadKey
  );

  const seen = new Set();
  const unique = [];
  for (const mail of matches) {
    const key = getMailDedupeKey(mail);
    if (seen.has(key)) {
      const existingIndex = unique.findIndex((item) => getMailDedupeKey(item) === key);
      if (existingIndex !== -1) {
        unique[existingIndex] = mergeMailDetails(unique[existingIndex], mail);
      }
      continue;
    }
    seen.add(key);
    unique.push({
      ...mail,
      replyText: extractReplyText(mail.body || mail.text || mail.preview || ''),
    });
  }

  for (let i = 0; i < unique.length; i += 1) {
    unique[i] = {
      ...unique[i],
      replyText: extractReplyText(unique[i].body || unique[i].text || unique[i].preview || ''),
    };
  }

  unique.sort((a, b) => (
    getMailTimestamp(a) - getMailTimestamp(b)
    || getMailOrderValue(a) - getMailOrderValue(b)
  ));
  return unique;
};

export const groupInboxByThread = (mails = [], userEmail) => {
  const mailByMessageId = buildMessageIdIndex(mails);
  const buckets = new Map();

  for (const mail of mails) {
    const threadKey = resolveThreadKey(mail, userEmail, mailByMessageId);
    if (!buckets.has(threadKey)) buckets.set(threadKey, []);
    buckets.get(threadKey).push(mail);
  }

  const grouped = [];
  for (const [threadKey, items] of buckets.entries()) {
    const sorted = [...items].sort((a, b) => (
      getMailTimestamp(b) - getMailTimestamp(a)
      || getMailOrderValue(b) - getMailOrderValue(a)
    ));
    const latest = sorted[0];
    grouped.push({
      ...latest,
      threadKey,
      threadCount: sorted.length,
      threadMailKeys: sorted.map(getMailDedupeKey),
    });
  }

  grouped.sort((a, b) => (
    getMailTimestamp(b) - getMailTimestamp(a)
    || getMailOrderValue(b) - getMailOrderValue(a)
  ));
  return grouped;
};

export const getConversationThreadMeta = (messages = []) => {
  const list = Array.isArray(messages) ? messages.filter(Boolean) : [];
  if (!list.length) return { threadId: '', inReplyTo: '', references: '' };

  const root = list[0];
  const latest = list[list.length - 1] || root;
  const threadId = String(root?.threadId || latest?.threadId || '').trim();
  const inReplyTo = normalizeMessageId(latest?.messageId || root?.messageId || '');
  const references = list
    .map((msg) => normalizeMessageId(msg?.messageId))
    .filter(Boolean)
    .join(' ');

  return { threadId, inReplyTo, references };
};
